import { ETipoVisualizacao } from "../../../../@types/IntegracaoCampoEnum";
import {
  SELECTED_OPTIONS_BY_FIELD_KEY,
  TIntegracaoCampoModel
} from "../../../../@types/IntegracaoCampoModel";
import useFieldLayout from "../useFieldLayout/useFieldLayout";

const SELECT_VALUE_KEYS = ["value", "Value", "_id", "$id", "id", "Id"];

const normalizeSelectValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value !== "object" || Array.isArray(value)) return String(value);

  const record = value as Record<string, unknown>;

  for (const key of SELECT_VALUE_KEYS) {
    const candidate = record[key];

    if (
      candidate !== null &&
      candidate !== undefined &&
      typeof candidate !== "object"
    )
      return String(candidate);
  }

  return "";
};

const normalizeBooleanValue = (value: unknown): boolean => {
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["false", "0", "nao", "não"].includes(normalized)) return false;
    if (["true", "1", "sim"].includes(normalized)) return true;
  }

  return Boolean(value);
};

const maskCpf = (value: unknown): string => {
  const digits = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 11);
  if (digits.length !== 11) return String(value ?? "");

  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

export default function useBuildInitialValues() {
  const { layoutType } = useFieldLayout();

  const initialValueByType = (field: TIntegracaoCampoModel): unknown => {
    const type = layoutType(field);
    const hasPersistedValue = field.valorPersistido !== undefined;
    const persistedValues = field.valorPersistido ?? [];
    const scalarValue = hasPersistedValue
      ? persistedValues[0]
      : field.valorPadrao;
    const listValue = hasPersistedValue ? persistedValues : field.valorPadrao;

    const listTypes = [
      ETipoVisualizacao.UPLOAD,
      ETipoVisualizacao.SELECT_MULTIPLE,
      ETipoVisualizacao.INPUT_TAG_NUMBER,
      ETipoVisualizacao.INPUT_TAG_TEXT,
      ETipoVisualizacao.CHECKBOX_GROUP,
      ETipoVisualizacao.REMOTE_COLLECTION
    ];

    if (listTypes.includes(type)) {
      if (type === ETipoVisualizacao.UPLOAD && hasPersistedValue) return [];
      if (!Array.isArray(listValue)) return [];
      if (type === ETipoVisualizacao.REMOTE_COLLECTION) return listValue;
      if (type === ETipoVisualizacao.SELECT_MULTIPLE)
        return listValue.map(normalizeSelectValue).filter(Boolean);

      return listValue.map(String);
    }

    switch (type) {
      case ETipoVisualizacao.CHECKBOX:
        return normalizeBooleanValue(scalarValue);
      case ETipoVisualizacao.NUMBER: {
        if (!hasPersistedValue) return field.valorPadrao ?? null;
        if (
          scalarValue === null ||
          scalarValue === undefined ||
          scalarValue === ""
        )
          return null;

        const numberValue = Number(scalarValue);

        return Number.isNaN(numberValue) ? null : numberValue;
      }
      case ETipoVisualizacao.DATE:
        return hasPersistedValue ? (scalarValue ?? null) : null;
      case ETipoVisualizacao.SELECT:
        return normalizeSelectValue(scalarValue);
      case ETipoVisualizacao.TEXT:
      case ETipoVisualizacao.PASSWORD:
      case ETipoVisualizacao.RADIO:
      case ETipoVisualizacao.BUTTON:
      default:
        if (field.mascara === "cpf") return maskCpf(scalarValue);

        return scalarValue != null ? String(scalarValue) : "";
    }
  };

  const buildInitialValues = (
    fields: TIntegracaoCampoModel[]
  ): Record<string, unknown> =>
    fields.reduce<Record<string, unknown>>((acc, field) => {
      acc[field.nomeCampo] = initialValueByType(field);
      acc[SELECTED_OPTIONS_BY_FIELD_KEY] =
        acc[SELECTED_OPTIONS_BY_FIELD_KEY] ?? {};

      return acc;
    }, {});

  return {
    buildInitialValues,
    initialValueByType
  };
}
